import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { useTheme } from '../ThemeContext';

interface MarketDummyProps {
    data: ChartData,
    periods?: string[],
    rangeVisible?: boolean,
    onChange?: (period: string) => void;
    selectedPeriod: string;
}
const MarketDummy: React.FC<MarketDummyProps> = ({ data, periods, rangeVisible = true, onChange, selectedPeriod = 'annual' }) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const [selectedRange, setSelectedRange] = useState<string>('All');
    // const [selectedPeriod, setSelectedPeriod] = useState<string>('annual');
    const [yearRange, setYearRange] = useState<[number, number]>([0, 100]);
    const sliderRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();


    useEffect(() => {
        return () => {
            if (chartRef.current && chartRef.current.chart) {
                chartRef.current.chart.destroy();
            }
        };
    }, []);
    // Highcharts options
    const options: Highcharts.Options = {
        accessibility: {
            enabled: false // Disable accessibility to avoid the error
        },
        chart: {
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Inter, sans-serif'
            },
            height: 500,
            alignTicks: false
        },
        credits: {
            enabled: true,
            text: 'Powered by StockFortress',
            href: 'https://stockfortress.com',
            style: {
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '10px'
            }
        },
        title: { text: '' },
        xAxis: [
            {
                categories: periods,
                labels: {
                    style: {
                        color: '#9CA3AF'
                    },
                    rotation: selectedPeriod === 'quarter' ? -45 : 0,
                    align: selectedPeriod === 'quarter' ? 'right' : 'center'
                },
                gridLineColor: '#374151',
                lineColor: '#374151',
                tickColor: '#374151',
            },
            {
                type: 'datetime',
                visible: false
            }
        ],
        yAxis: data.yAxis?.map(axis => ({
            ...axis,
            stackLabels: {
                enabled: false
            },
            accessibility: {
                enabled: false
            }
        })) || [],
        series: data.series?.map(series => ({
            ...series,
            stacking: undefined,
            accessibility: {
                enabled: false
            }
        })) || [],
        // rangeSelector: undefined,
        rangeSelector: {
            enabled: true,
        },
        navigator: {
            enabled: true,
        },
        scrollbar: {
            enabled: true,
        },
        tooltip: {
            shared: true,
            backgroundColor: theme === 'dark' ? '#1F2937' : '#ffffff',
            borderColor: '#374151',
            style: {
                color: theme === 'dark' ? '#ffffff' : '#000000'
            },
            formatter: function () {
                let tooltip = ``;
                if (this.options.label == 'share_price')
                    tooltip = `<b>${moment(this.x).format('DD MMM YYYY')}</b><br/>`;
                else
                    tooltip = `<b>${this.category}</b><br/>`;
                this.points?.forEach(point => {
                    const seriesName = point.series.name;
                    let value = point.y ?? 0;
                    let unit = '';
                    if (this.options.label === 'share_price') {
                        unit = '$';
                    } else {
                        unit = '%';
                    }

                    tooltip += `<span style="color:${point.color}">${seriesName}</span>: <b>${Highcharts.numberFormat(value, 2)}${unit}</b><br/>`;
                });

                return tooltip;
            }
        },
        legend: {
            enabled: true,
            layout: 'horizontal',
            verticalAlign: 'bottom',
            floating: false,
            align: 'center',
            itemStyle: {
                color: theme === 'dark' ? 'white' : 'black',
                fontSize: '12px',
                fontWeight: 'normal',
                textOutline: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0)',
            },
            itemHoverStyle: {
                color: Highcharts.getOptions().colors?.[0].toString() || '#7cb5ec',
            }
        },
        plotOptions: {
            column: {
                borderRadius: 4,
                borderWidth: 0,
                pointPadding: 0.1,
                groupPadding: 0.2,
                // pointWidth: 6, // further decrease column width
                dataLabels: {
                    enabled: false
                }
            },
            line: {
                lineWidth: 2,
                marker: {
                    radius: 4,
                    lineWidth: 2,
                    lineColor: '#ffffff'
                }
            }
        },
    };

    const periodOptions = [
        { value: 'annual', label: 'Yearly' },
        { value: 'quarter', label: 'Quarterly' }
    ];

    const rangeOptions = [
        { value: '1y', label: '1 Year (TTM)' },
        { value: '5y', label: '5 Years' },
        { value: '7y', label: '7 Years' },
        { value: 'All', label: 'All' }
    ];

    const handlePeriodChange = (period: string) => {
        // setSelectedPeriod(period);
        if (onChange) {
            onChange(period);
        }
    };

    const handleRangeChange = (range: string) => {
        setSelectedRange(range);
        if (range !== 'All') {
            setYearRange([0, 100]); // Reset year slider when using preset ranges
        }
    };

    const handleYearRangeChange = (index: number, value: number) => {
        const newRange: [number, number] = [...yearRange];
        newRange[index] = value;

        // Ensure start is not greater than end
        if (index === 0 && newRange[0] > newRange[1]) {
            newRange[1] = newRange[0];
        }
        if (index === 1 && newRange[1] < newRange[0]) {
            newRange[0] = newRange[1];
        }

        setYearRange(newRange);
        setSelectedRange('All');
    };

    const resetYearRange = () => {
        setYearRange([0, 100]);
        setSelectedRange('All');
    };

    const handleSliderClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickPercent = (clickX / rect.width) * 100;

        const distanceToStart = Math.abs(clickPercent - yearRange[0]);
        const distanceToEnd = Math.abs(clickPercent - yearRange[1]);

        if (distanceToStart < distanceToEnd) {
            handleYearRangeChange(0, Math.max(0, Math.min(100, clickPercent)));
        } else {
            handleYearRangeChange(1, Math.max(0, Math.min(100, clickPercent)));
        }
    };

    return (
        <div className="financial-combo-chart-container" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: theme === 'dark' ? '#1F2937' : '#ffffff',
            borderRadius: '8px'
        }}>
            {rangeVisible && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '20px'
                }}>
                    {/* Period Buttons */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <label style={{
                            color: '#9CA3AF',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}>
                            Period:
                        </label>
                        {periodOptions.map(option => (
                            <button
                                key={option.value}
                                onClick={() => handlePeriodChange(option.value)}
                                className={`btn${selectedPeriod === option.value ? ' active' : ''}`}
                                style={{
                                    padding: '6px 12px',
                                    // backgroundColor: selectedPeriod === option.value ? '#4F46E5' : 'transparent',
                                    // color: selectedPeriod === option.value ? 'white' : '#9CA3AF',
                                    // border: '1px solid #4B5563',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            // onMouseEnter={(e) => {
                            //     if (selectedPeriod !== option.value) {
                            //         e.currentTarget.style.backgroundColor = '#374151';
                            //         e.currentTarget.style.color = 'white';
                            //     }
                            // }}
                            // onMouseLeave={(e) => {
                            //     if (selectedPeriod !== option.value) {
                            //         e.currentTarget.style.backgroundColor = 'transparent';
                            //         e.currentTarget.style.color = '#9CA3AF';
                            //     }
                            // }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {/* 
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <label style={{
                            color: '#9CA3AF',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}>
                            Range:
                        </label>
                        {rangeOptions.map(option => (
                            <button
                                key={option.value}
                                onClick={() => handleRangeChange(option.value)}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: selectedRange === option.value ? '#4F46E5' : 'transparent',
                                    color: selectedRange === option.value ? 'white' : '#9CA3AF',
                                    border: '1px solid #4B5563',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (selectedRange !== option.value) {
                                        e.currentTarget.style.backgroundColor = '#374151';
                                        e.currentTarget.style.color = 'white';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (selectedRange !== option.value) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#9CA3AF';
                                    }
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div> */}
                </div>)}
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                ref={chartRef}
            />
        </div>
    );
};

export default MarketDummy;